(() => {
  const normalize = (value) => String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  const consecutiveWordScore = (left, right) => {
    const leftWords = normalize(left).split(" ").filter(Boolean);
    const rightWords = normalize(right).split(" ").filter(Boolean);
    let best = 0;

    for (let leftIndex = 0; leftIndex < leftWords.length; leftIndex += 1) {
      for (let rightIndex = 0; rightIndex < rightWords.length; rightIndex += 1) {
        let length = 0;
        while (leftWords[leftIndex + length] && leftWords[leftIndex + length] === rightWords[rightIndex + length]) length += 1;
        best = Math.max(best, length);
      }
    }

    return best;
  };
  const findProductData = (entries, productTitle) => {
    const exactMatch = entries.find((entry) => normalize(entry.name) === normalize(productTitle));
    if (exactMatch) return exactMatch;

    return entries
      .map((entry) => ({ entry, score: consecutiveWordScore(entry.name, productTitle) }))
      .filter(({ score }) => score >= 4)
      .sort((left, right) => right.score - left.score)[0]?.entry;
  };
  const sizeNumber = (value) => {
    const match = String(value || "").match(/(\d+(?:\.\d+)?)\s*cm\b/i);
    return match ? String(Number(match[1])) : "";
  };

  class CottonfySofaFitChecker extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === "true") return;
      this.dataset.ready = "true";
      this.dialog = this.querySelector("dialog");
      this.form = this.querySelector("[data-cottonfy-fit-form]");
      this.result = this.querySelector("[data-cottonfy-fit-result]");
      this.sizeOutput = this.querySelector("[data-cottonfy-fit-size]");
      this.variantData = JSON.parse(this.querySelector("[data-cottonfy-fit-variants]")?.textContent || "[]");
      this.productData = findProductData(window.CottonfySofaPackagingData || [], this.dataset.productTitle);
      if (!this.dialog || !this.form) return;
      this.installTrigger();
      this.bindEvents();
      this.updateSelection();
    }

    installTrigger() {
      const productRoot = document.getElementById(`product-info-${this.dataset.productFormId.replace("product-form-", "")}`)?.closest("product-rerender") || document.querySelector(`[observe-form="${this.dataset.productFormId}"]`);
      if (!productRoot) return;
      const block = productRoot.querySelector("[data-cottonfy-fit-block]");
      const link = block?.querySelector('a[href^="#cottonfy-sofa-fit"]');
      if (!block || !link) return;
      block.hidden = false;
      link.href = `#${this.dataset.dialogId}`;
      link.setAttribute("data-cottonfy-fit-open", "");
    }

    bindEvents() {
      document.addEventListener("click", (event) => {
        const opener = event.target.closest(`[data-cottonfy-fit-open], a[href="#${this.dataset.dialogId}"]`);
        if (!opener) return;
        event.preventDefault();
        this.updateSelection();
        this.result.hidden = true;
        this.dialog.showModal();
      });
      this.querySelector("[data-cottonfy-fit-close]")?.addEventListener("click", () => this.dialog.close());
      this.dialog.addEventListener("click", (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });
      this.form.addEventListener("submit", (event) => {
        event.preventDefault();
        this.checkFit();
      });
      document.addEventListener("variant:change", (event) => {
        if (event.detail?.formId !== this.dataset.productFormId) return;
        this.selectedVariant = event.detail.variant;
        this.updateSelection();
      });
      document.addEventListener("shopify:section:load", () => this.installTrigger());
    }

    currentVariant() {
      if (this.selectedVariant) return this.selectedVariant;
      const form = document.getElementById(this.dataset.productFormId);
      const id = form?.querySelector('[name="id"]')?.value;
      return this.variantData.find((variant) => String(variant.id) === String(id)) || this.variantData[0];
    }

    selectedPackaging() {
      if (!this.productData) return null;
      const variant = this.currentVariant();
      const values = [...(variant?.options || []), variant?.title || ""];
      const availableSizes = Object.keys(this.productData.sizes);
      for (const value of values) {
        const parsed = sizeNumber(value);
        if (parsed && availableSizes.includes(parsed)) return { size: parsed, data: this.productData.sizes[parsed] };
      }
      return null;
    }

    updateSelection() {
      const packaging = this.selectedPackaging();
      this.currentPackaging = packaging;
      this.sizeOutput.textContent = packaging ? `${packaging.size} cm · ${this.dataset.packagesCount.replace("__COUNT__", packaging.data.count)}` : this.dataset.unavailable;
      this.form.querySelector("button[type='submit']").disabled = !packaging;
      this.result.hidden = true;
    }

    packagePasses(dimensions, measurements, clearance) {
      const permutations = [
        [dimensions[0], dimensions[1], dimensions[2]],
        [dimensions[0], dimensions[2], dimensions[1]],
        [dimensions[1], dimensions[0], dimensions[2]],
        [dimensions[1], dimensions[2], dimensions[0]],
        [dimensions[2], dimensions[0], dimensions[1]],
        [dimensions[2], dimensions[1], dimensions[0]]
      ];
      return permutations.some(([width, height, depth]) => (
        width + clearance <= measurements.doorWidth &&
        height + clearance <= measurements.doorHeight &&
        Math.min(width, height, depth) + clearance <= measurements.routeWidth &&
        Math.min(Math.max(width, depth), Math.max(height, depth)) + clearance <= measurements.turningSpace
      ));
    }

    checkFit() {
      if (!this.currentPackaging || !this.form.reportValidity()) return;
      const formData = new FormData(this.form);
      const measurements = {
        doorWidth: Number(formData.get("door_width")),
        doorHeight: Number(formData.get("door_height")),
        routeWidth: Number(formData.get("route_width")),
        turningSpace: Number(formData.get("turning_space"))
      };
      const packages = this.currentPackaging.data.packages.map((dimensions, index) => ({
        index: index + 1,
        dimensions,
        comfortable: this.packagePasses(dimensions, measurements, 5),
        possible: this.packagePasses(dimensions, measurements, 2)
      }));
      const allComfortable = packages.every((item) => item.comfortable);
      const allPossible = packages.every((item) => item.possible);
      const status = allComfortable ? "pass" : allPossible ? "tight" : "fail";
      const title = this.dataset[`result${status[0].toUpperCase()}${status.slice(1)}Title`];
      const text = this.dataset[`result${status[0].toUpperCase()}${status.slice(1)}Text`];
      const packageRows = packages.map((item) => {
        const label = item.possible ? this.dataset.passLabel : this.dataset.failLabel;
        const state = item.possible ? "pass" : "fail";
        return `<li><span><strong>${this.dataset.packageLabel} ${item.index}</strong><small>${item.dimensions.join(" × ")} cm</small></span><span class="cottonfy-fit-result__package-state cottonfy-fit-result__package-state--${state}">${label}</span></li>`;
      }).join("");
      this.result.className = `cottonfy-fit-result cottonfy-fit-result--${status}`;
      this.result.innerHTML = `<div class="cottonfy-fit-result__summary"><span aria-hidden="true"></span><div><h3 class="h5">${title}</h3><p>${text}</p></div></div><ul>${packageRows}</ul>`;
      this.result.hidden = false;
      this.result.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }

  if (!customElements.get("cottonfy-sofa-fit-checker")) customElements.define("cottonfy-sofa-fit-checker", CottonfySofaFitChecker);
})();
